import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, ArrowRight } from 'lucide-react';
import { Input } from '../ui/input';
import { listingsAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { CATEGORIES, getLocalizedName } from '../../data/categories';
import { SearchSuggestions } from './SearchSuggestions';

/**
 * Global search bar with typeahead suggestions.
 * - Debounced (250ms)
 * - Keyboard accessible (↑ ↓ Enter Esc)
 * - Click a suggestion -> navigate to listing
 * - "Search for ..." footer -> go to full /search?q=
 */
const DROPDOWN_CLASS = 'fixed inset-x-2 sm:absolute sm:inset-x-0 sm:top-full sm:mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[70vh] overflow-y-auto';

const SearchAutocomplete = ({ className = '' }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const inflightRef = useRef(0);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Debounced fetch
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const reqId = ++inflightRef.current;
    setLoading(true);
    try {
      const data = await listingsAPI.suggest(q.trim(), 8);
      // Only apply the most recent response
      if (reqId === inflightRef.current) {
        setSuggestions(data || []);
        setHighlighted(-1);
      }
    } catch (err) {
      if (reqId === inflightRef.current) setSuggestions([]);
    } finally {
      if (reqId === inflightRef.current) setLoading(false);
    }
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  };

  const goToSearch = () => {
    if (!query.trim()) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const goToListing = (id) => {
    setOpen(false);
    setQuery('');
    navigate(`/listing/${id}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (highlighted >= 0 && highlighted < suggestions.length) {
      goToListing(suggestions[highlighted].id);
    } else {
      goToSearch();
    }
  };

  const onKeyDown = (e) => {
    if (!open) return;
    const items = suggestions.length + (query.trim() ? 1 : 0); // +1 = "search for" footer
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? items - 1 : h - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const formatPrice = (price, priceType) => {
    if (priceType === 'free') return t('listing.free') || 'Free';
    if (priceType === 'on-demand') return t('listing.onDemand') || 'On request';
    if (!price && price !== 0) return '';
    return `€${Number(price).toLocaleString('fr-FR')}`;
  };

  const isFooterHighlighted = highlighted === suggestions.length;

  // On phones the input is narrow, so the dropdown is fixed to the viewport just under the input
  const dropdownStyle = () => {
    if (typeof window === 'undefined' || window.innerWidth >= 640 || !wrapperRef.current) return undefined;
    return { top: wrapperRef.current.getBoundingClientRect().bottom + 8 };
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <form onSubmit={onSubmit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 pointer-events-none" />
          <Input
            type="text"
            value={query}
            onChange={onChange}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={t('header.searchPlaceholder')}
            className="w-full pl-9 sm:pl-10 pr-10 py-2 sm:py-2.5 h-10 sm:h-auto bg-gray-50 border-gray-200 rounded-full focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm sm:text-base"
            autoComplete="off"
            data-testid="search-autocomplete-input"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500 animate-spin" />
          )}
        </div>
      </form>

      {open && query.trim().length < 2 && (
        <div className={DROPDOWN_CLASS} style={dropdownStyle()} data-testid="search-empty-dropdown">
          <SearchSuggestions compact onNavigate={() => setOpen(false)}
            onPickTerm={(term) => { setOpen(false); setQuery(''); navigate(`/search?q=${encodeURIComponent(term)}`); }} />
        </div>
      )}

      {open && query.trim().length >= 2 && (suggestions.length > 0 || !loading) && (
        <div
          className={DROPDOWN_CLASS} style={dropdownStyle()}
          data-testid="search-autocomplete-dropdown"
        >
          {suggestions.length === 0 && !loading && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {t('search.noSuggestions') || 'No matches yet. Try the full search:'}
            </div>
          )}

          {suggestions.map((s, idx) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goToListing(s.id)}
              onMouseEnter={() => setHighlighted(idx)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                highlighted === idx ? 'bg-emerald-50' : 'hover:bg-gray-50'
              }`}
              data-testid={`search-suggestion-${idx}`}
            >
              <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {s.thumb ? (
                  <img src={s.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <Search className="h-5 w-5 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{s.title}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">{(() => { const c = CATEGORIES.find((x) => x.id === s.category); return c ? getLocalizedName(c, language) : s.category; })()}</span>
                  {s.location && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="truncate">
                        {typeof s.location === 'string' ? s.location : s.location?.name}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-sm font-semibold text-emerald-600 flex-shrink-0">
                {formatPrice(s.price, s.price_type)}
              </div>
            </button>
          ))}

          {query.trim() && (
            <button
              type="button"
              onClick={goToSearch}
              onMouseEnter={() => setHighlighted(suggestions.length)}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium border-t border-gray-100 transition-colors ${
                isFooterHighlighted ? 'bg-emerald-50 text-emerald-700' : 'text-emerald-600 hover:bg-emerald-50'
              }`}
              data-testid="search-autocomplete-see-all"
            >
              <span className="truncate">
                {t('search.seeAllFor') || 'See all results for'} <span className="font-semibold">“{query}”</span>
              </span>
              <ArrowRight className="h-4 w-4 ml-2 flex-shrink-0" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
